import ShapeFactory from "@tosize/shapes/src/lib/factory/ShapeFactory";
import type InputType from "@tosize/shapes/src/lib/models/Input/InputType";
import type { JsonInputType } from "@tosize/shapes/src/lib/models/Input/JsonInputType";
import type { ShapeInput } from "@tosize/shapes/src/types/shape";
import { useCallback, useMemo } from "react";
import { type FieldErrorType, useErrorContext } from "../context/ErrorContext";
import { usePostDataContext } from "../context/PostDataContext";
import useProducts from "./useProducts";

const useDimensionsForm = (shape?: ShapeInput) => {
	const { resetErrors, setTouchedFields, errorMap, addErrors } = useErrorContext();
	const { workpiece, data } = usePostDataContext();
	const { productData } = useProducts();

	const shapeModel = useMemo(() => {
		return shape && productData
			? ShapeFactory.getInstance(shape, data.unit, productData)
			: undefined;
	}, [shape, productData, data.unit]);

	// Memoize the validation schema, beacuse re-creating it on every render isn't necessary
	const schema = useMemo(() => {
		return shapeModel ? shapeModel.getValidationSchema() : undefined;
	}, [shapeModel]);

	type ValidateProps = {
		fields: JsonInputType[];
		values?: InputType;
		silent?: true;
	};

	const validate = useCallback(
		async ({ fields, values, silent }: ValidateProps) => {
			resetErrors("dimensions");

			// Add the current input to the touched fields. Every field touched by the user
			// should have a validation message
			const result = await schema?.safeParseAsync(values ?? workpiece.shape_dimensions, {
				errorMap,
			});

			if (result) {
				if (result.success === false) {
					const fieldErrors: FieldErrorType = {};

					// Loop over every touched field and add the errors from the schema validation
					// every field that has not been touched yet, or when silent mode is enabled, should not display any errors
					for (const fieldId of fields) {
						const issues = result.error.issues.filter((issue) => issue.path.includes(fieldId));

						if (issues.length) fieldErrors[fieldId] = issues;
					}

					addErrors("dimensions", fieldErrors);
				}

				!silent && setTouchedFields((prev) => ({ ...prev, dimensions: fields }));

				return result.success;
			}

			return true;
		},
		[addErrors, workpiece.shape_dimensions, errorMap, resetErrors, schema, setTouchedFields],
	);

	return {
		validate,
		shapeModel,
		schema,
	};
};

export default useDimensionsForm;
